// normalized, as advised in https://redux.js.org/usage/structuring-reducers/normalizing-state-shape

import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import Api from "../api/api";
import { updateUserAttributes } from "../api/cognitoMfa";
import { logIn, logOut } from "./globalSlice";
import { addEntityFetchReducers, addEntityUpdateReducers, EntityState } from "./entity";
import { Subscription, User } from "../api/models";
import { RootState } from "./store";
import { useFetchableState } from "./fetchableState";

interface UsersState extends EntityState<User> {
    collections: { [key: string]: any };
}

const initialState: UsersState = {
    entities: {},
    collections: {}
}

export const usersSlice = createSlice({
    name: 'users',
    initialState: initialState,
    reducers: {},
    extraReducers(builder) {
        addEntityFetchReducers<
            User,
            { user: User },
            { id: string, accessToken: string }
        >(
            builder,
            (payload) => payload.user,
            fetchUserThunk
        )
        addEntityUpdateReducers<User, { id: string, updates: Partial<User>, accessToken: string }>(
            builder,
            setUserAccessThunk,
            (payload) => payload.user
        )
        addEntityUpdateReducers<User, { id: string, updates: Partial<User>, accessToken: string }>(
            builder,
            updateProfileThunk,
            (payload) => payload.user
        )
        builder.addCase(logIn, (state, action: PayloadAction<{ user: User, subscription?: Subscription }>) => {
            const user = action.payload.user;
            state.entities[user.id] = {
                data: user,
                lastFetched: Date.now()
            };
        })
        builder.addCase(logOut, (state: UsersState) => {
            state = initialState;
        })
    }
})

const fetchUserThunk = createAsyncThunk(
    'users/fetchUser',
    async ({ id, accessToken }: { id: string, accessToken: string }, { getState, requestId }) => {
        const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
        const currentRequestId = state.users.entities[id]?.currentRequestId
        if (currentRequestId !== requestId)
            throw new Error("Request outdated");
        return await Api.getUser({ userId: id, accessToken });
    }
)

// Optimistic toggle of a user's Cognito account access (admin-only endpoint):
// the entity's enabled flag flips immediately and rolls back on failure
// (addEntityUpdateReducers keeps oldData for that).
export const setUserAccessThunk = createAsyncThunk(
    'users/setUserAccess',
    async ({ id, updates, accessToken }: { id: string, updates: Partial<User>, accessToken: string }, { getState, requestId }) => {
        const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
        const currentRequestId = state.users.entities[id]?.currentRequestId
        if (currentRequestId !== requestId)
            throw new Error("Request outdated");
        return await Api.setUserAccess({ userId: id, enabled: updates.enabled === true, accessToken });
    }
)

// Optimistic write of the caller's own profile attributes (name, picture),
// straight from the browser to Cognito — there is no backend route for this.
// Cognito returns no user object, so fulfilled resolves with the entity
// merged from state; addEntityUpdateReducers rolls back on failure.
export const updateProfileThunk = createAsyncThunk(
    'users/updateProfile',
    async ({ id, updates, accessToken }: { id: string, updates: Partial<User>, accessToken: string }, { getState, requestId }) => {
        const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
        const entity = state.users.entities[id]
        if (entity?.currentRequestId !== requestId)
            throw new Error("Request outdated");
        const attributes: { Name: string, Value: string }[] = []
        if (updates.firstname !== undefined) attributes.push({ Name: "given_name", Value: updates.firstname })
        if (updates.lastname !== undefined) attributes.push({ Name: "family_name", Value: updates.lastname })
        if (updates.imageUrl !== undefined) attributes.push({ Name: "picture", Value: updates.imageUrl })
        await updateUserAttributes(accessToken, attributes)
        // the pending reducer already applied `updates` optimistically;
        // entity.data reflects them, merging again is a no-op safeguard
        return { user: { ...entity.data!, ...updates } }
    }
)

export function useUser(userId: string, accessToken: string) {
    return useFetchableState<User>({
        selector: (state: RootState) => state.users.entities[userId],
        action: fetchUserThunk({ id: userId, accessToken })
    })
}

export default usersSlice.reducer